import { useState, useCallback, useMemo, useEffect, memo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Typography, makeStyles } from '@material-ui/core';
import PageContainer from '../../shared/pageContainer';
import NotificationCard from '../../shared/notificationCard';
import Search from '../../shared/search';
import CustomButton from '../../shared/customButton';
import CustomFormDrawer from '../../shared/customFormDrawer';
import Filters from '../../shared/filters';
import StatusIndicator from '../../shared/statusIndicator';
import AlertDialog from '../../shared/alertDialog';
import CyclesTable from '../../shared/cyclesTable';
import ActionButton from '../../shared/actionButton';
import ChooseNameDialog from '../../shared/chooseNameDialog';
import PreviewAttributeDialog from '../../shared/previewAttributeDialog';
import { ReactComponent as CyclesIcon } from '../../../assets/icons/sync.svg';
import { SYSTEM_ROLES, SYSTEM_ROLE_OPTIONS } from '../../../constants/roles';
import { PERMISSION_CODES } from '../../../constants/permissions';
import { ACTION_BUTTON_TYPES } from '../../shared/actionButton/config';
import { CYCLE_STATUSES } from '../../../constants/statuses';
import {
  APP_PAGES,
  PARAMS,
  CYCLES_DEFAULT_PARAMS,
  PAGE_WHITELISTED_PARAMS,
} from '../../../constants/pages';
import { SURVEY_TITLE_FIELD } from '../../../constants/survey';
import { ATTRIBUTE_TYPES_MAP } from '../attributesPage/config';
import {
  CYCLE_STEP_TYPES,
  MANAGE_CYCLE_FIELD_NAMES,
  INITIAL_SORT,
} from '../../../constants/cycle';
import { sticky } from '../../../constants/helperCssRules';
import {
  API_CYCLES,
  api_cycle,
  api_cycle_steps,
  api_cycle_step,
  api_survey_send_reminder,
  api_survey,
  api_attribute,
} from '../../../constants/apiRoutes';
import { surveysSelector } from '../../../store/selectors/surveysSelector';
import { cyclesSelector } from '../../../store/selectors/cyclesSelector';
import { jobTitlesSelector } from '../../../store/selectors/jobTitlesSelector';
import { levelsAndTracksSelector } from '../../../store/selectors/levelsAndTracksSelector';
import { divisionsAndDepartmentsSelector } from '../../../store/selectors/divisionsAndDepartmentsSelector';
import { tagsSelector } from '../../../store/selectors/tagsSelector';
import { preparePeopleFilters } from '../../../utility/people';
import { prepareRoles } from '../../../utility/roles';
import {
  getCycles,
  clearCycles,
  getCycleReviews,
} from '../../../store/modules/cycles';
import {
  getAttributesWithQuestions,
  clearAttributesWithQuestions,
  getSurveyTrackingById,
} from '../../../store/modules/surveys';
import { getJobTitles, clearJobTitles } from '../../../store/modules/jobTitles';
import {
  getTagsCategories,
  clearTagsCategories,
} from '../../../store/modules/tags';
import {
  getLevelsAndTracks,
  clearLevelsAndTracks,
} from '../../../store/modules/levelsAndTracks';
import {
  getDivisionsAndDepartments,
  clearDivisionsAndDepartments,
} from '../../../store/modules/divisionsAndDepartments';
import { setIsInlineSurvey } from '../../../store/modules/fillSurvey';
import { useTranslations } from '../../../utility/useTranslations';
import {
  isArray,
  isArrayEmpty,
  getObjectToNumberArray,
  trimString,
  getActiveUsers,
  isObjectEmpty,
  getItemById,
} from '../../../utility/helpers';
import { hasAuthPermissionCheck } from '../../../utility/permissions';
import {
  parseDuplicateParameters,
  parseQueryParams,
  showSuccessMessage,
  hasNextPage,
  hasSelectedFiltersCheck,
} from '../../../utility/uiUtils';
import {
  prepareCycleSteps,
  prepareCycleInitialData,
  prepareManageCycleFormColumns,
  prepareStepForSave,
  prepareCycleTableHeaders,
  prepareCycleHeaderActions,
  prepareCycleTableData,
  prepareCycleStatuses,
  prepareCyclesPageFilters,
  prepareIntervalOptions,
  prepareCycleReviewTypes,
} from '../../../utility/cycles';
import { useAvailableUsers } from '../../../utility/hooks';
import http from '../../../utility/http';

const useStyles = makeStyles(
  ({ palette: { primary }, spacing, breakpoints }) => ({
    stickyHeader: {
      padding: spacing(8, 0, 4, 0),
      ...sticky(primary.white, 105),
    },
    description: {
      marginBottom: spacing(6),
    },
    actionWrapper: {
      display: 'flex',
      justifyContent: 'space-between',
    },
    actions: {
      display: 'flex',
      alignSelf: 'center',
      alignItems: 'center',
      marginLeft: 'auto',
    },
    filters: {
      display: 'flex',
      justifyContent: 'space-between',
    },
    search: {
      width: 260,
    },
    addCycleButton: {
      marginLeft: spacing(2),
      width: 'max-content',
    },
    table: {
      '& .grid-table-header': {
        ...sticky(primary.bluish8, 235),
      },
      '& .title-cell, & .start-at-cell, & .end-at-cell, & .cycle-users-cell, & .timeline-cell':
        {
          '& .row-cell-value': {
            color: primary.bluish1,
          },
        },
      '& .actions-cell': {
        overflow: 'visible',
        paddingLeft: 0,
        paddingTop: spacing(2),
        paddingBottom: spacing(2),
        paddingRight: spacing(1),

        [breakpoints.up('xLg')]: {
          paddingRight: spacing(3),
        },
      },
    },
    cycleTitle: {
      fontSize: 24,
      lineHeight: '28px',
    },
    cycleFormHeader: {
      padding: spacing(5, 8, 5, 5),
      maxHeight: 74,
      [breakpoints.up('xLg')]: {
        maxHeight: 74,
        padding: spacing(5, 8, 5, 7.75),
      },
    },
    columns: {
      gridTemplateColumns: 'repeat(2, minmax(340px, 1fr)) minmax(0, 2fr)',
      height: 'calc(100vh - 147px)',
      [breakpoints.up('xLg')]: {
        gridTemplateColumns: 'repeat(2, minmax(377px, 1fr)) minmax(0, 2fr)',
      },
      '& .columnContent ,& .secondarySettingsColumnContent': {
        padding: spacing(6, 6, 0),
        [breakpoints.up('xLg')]: {
          padding: spacing(8, 9, 0, 9),
        },
      },
    },
    secondarySettingsColumn: {
      display: 'grid',
      gridColumnGap: 8,
      gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
      alignItems: 'start',
      gridTemplateRows: 'max-content',
      gridTemplateAreas: `
      "subtitle subtitle subtitle subtitle subtitle"
      "users users users users users"
      "startDate startDate startDate startTime startTime"
      "endDate endDate endDate endTime endTime"
      "subject subject subject subject subject"
      "message message message message message"
      "title title title title title"
      "stepEmail stepEmail stepEmail stepEmail stepEmail"
      "stepEmailContent stepEmailContent stepEmailContent stepEmailContent stepEmailContent"
      "attributes attributes attributes attributes attributes"
      "participants participants participants participants participants"
      "repeatTitle repeatTitle repeatTitle repeatTitle repeatTitle"
      "repeatDescription repeatDescription repeatDescription repeatDescription repeatDescription"
      "intervalUnit intervalUnit intervalPeriod intervalPeriod intervalPeriod"
      "disabledFilter disabledFilter disabledFilter disabledFilter disabledFilter"
    `,
    },
    repeatTitle: {
      marginBottom: spacing(3),
      gridArea: 'repeatTitle',
    },
    repeatDescription: {
      gridArea: 'repeatDescription',
    },
    intervalUnit: {
      gridArea: 'intervalUnit',
    },
    intervalUnitField: {
      minWidth: 92,
    },
    intervalPeriodField: {
      minWidth: 142,
    },
    intervalPeriod: {
      gridArea: 'intervalPeriod',
    },
    attributesLabel: {
      fontFamily: 'ProximaNova-Bold',
      fontSize: 18,
      lineHeight: '20px',
      marginBottom: spacing(6),
    },
    attributes: {
      gridArea: 'attributes',
    },
    title: {
      gridArea: 'title',
    },
    subtitle: {
      gridArea: 'subtitle',
    },
    users: {
      gridArea: 'users',
    },
    startDate: {
      gridArea: 'startDate',
    },
    startTime: {
      paddingTop: spacing(6.5),
      gridArea: 'startTime',
    },
    endDate: {
      gridArea: 'endDate',
    },
    endTime: {
      paddingTop: spacing(6.5),
      gridArea: 'endTime',
    },
    subject: {
      gridArea: 'subject',
    },
    message: {
      gridArea: 'message',
    },
    stepEmail: {
      gridArea: 'stepEmail',
    },
    stepEmailContent: {
      gridArea: 'stepEmailContent',
    },
    participants: {
      gridArea: 'participants',
    },
    timePicker: {
      zIndex: 9996,
    },
    rowActions: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 30px)',
    },
    disabledFilter: {
      marginTop: spacing(6),
      gridArea: 'disabledFilter',
    },
  })
);

const CyclesPage = ({
  location,
  navigate,
  auth,
  accessControl,
  organizationSettings,
  dispatch,
  setDialogVisibility,
  ...rest
}) => {
  const classes = useStyles();
  const translations = useTranslations(APP_PAGES.CYCLES_PAGE);
  const { page: defaultPage } = CYCLES_DEFAULT_PARAMS;
  const { PAGE, ORDERING, SEARCH, STATUS, CREATOR } = PARAMS;
  const { EMAIL_STEP, SELF_REVIEW_STEP, TOP_DOWN_STEP, DOWN_UP_STEP } =
    CYCLE_STEP_TYPES;
  const { TITLE, USERS, IS_REPEATING, REPEAT_INTERVAL, REPEAT_INTERVAL_UNIT } =
    MANAGE_CYCLE_FIELD_NAMES;

  const allEmployeesParams = useMemo(
    () => ({
      [PARAMS.EXCLUDE_ROLE]: SYSTEM_ROLES.ASSOCIATE,
    }),
    []
  );
  const allEmployees = useAvailableUsers(allEmployeesParams, null);
  const { attributesWithQuestions } = useSelector(surveysSelector);
  const cycles = useSelector(cyclesSelector);
  const { jobTitles } = useSelector(jobTitlesSelector);
  const { tracks } = useSelector(levelsAndTracksSelector);
  const { divisions, departments } = useSelector(
    divisionsAndDepartmentsSelector
  );
  const { categories } = useSelector(tagsSelector);

  const [isInitialLoad, setIsInitialLoad] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [ordering, setOrdering] = useState(INITIAL_SORT);
  const [selectedFilters, setSelectedFilters] = useState({
    [STATUS]: [],
    [CREATOR]: [],
  });
  const [isManageCycleOpened, setIsManageCycleOpened] = useState(false);
  const [isDeleteCycleOpened, setIsDeleteCycleOpened] = useState(false);
  const [selectedCycle, setSelectedCycle] = useState(null);
  const [selectedCycleId, setSelectedCycleId] = useState(null);
  const [activeSteps, setActiveSteps] = useState({
    [EMAIL_STEP]: false,
    [SELF_REVIEW_STEP]: false,
    [TOP_DOWN_STEP]: false,
    [DOWN_UP_STEP]: false,
  });
  const [selectedReview, setSelectedReview] = useState(null);
  const [isReviewTitleDialogOpened, setIsReviewTitleDialogOpened] =
    useState(false);
  const [isPreviewAttributeOpened, setIsPreviewAttributeOpened] =
    useState(false);
  const [attribute, setAttribute] = useState(null);

  const canManage = hasAuthPermissionCheck(
    PERMISSION_CODES.CYCLES_MANAGE,
    accessControl
  );
  const activeEmployees = useMemo(
    () => getActiveUsers(allEmployees),
    [allEmployees]
  );
  const gridHeaderActions = prepareCycleHeaderActions(translations.columns);
  const gridHeaders = prepareCycleTableHeaders(translations.columns);
  const hasLoadMore = hasNextPage(cycles);
  const isActiveFilter = hasSelectedFiltersCheck(selectedFilters);
  const statuses = prepareCycleStatuses(translations.statuses);
  const filters = prepareCyclesPageFilters(
    translations,
    statuses,
    allEmployees
  );
  const cycleSteps = prepareCycleSteps(translations.cycleSteps);
  const intervalOptions = prepareIntervalOptions(translations.repeatInterval);
  const reviewTypes = prepareCycleReviewTypes(translations.cycleReviewTypes);

  const initialCycleFormData = prepareCycleInitialData(
    translations.manageCycle,
    cycleSteps,
    activeEmployees,
    selectedCycle
  );

  const peopleFilters = useMemo(
    () =>
      !isInitialLoad
        ? preparePeopleFilters(
            translations,
            jobTitles,
            tracks,
            divisions,
            departments,
            prepareRoles(translations.systemRoles, SYSTEM_ROLE_OPTIONS, true),
            activeEmployees,
            categories
          )
        : [],
    [
      translations,
      isInitialLoad,
      jobTitles,
      tracks,
      divisions,
      departments,
      categories,
      activeEmployees,
    ]
  );

  const handleOpenAttributePreview = useCallback(async attributeId => {
    const { data } = await http.get(api_attribute(attributeId));

    setIsPreviewAttributeOpened(true);
    setAttribute(data);
  }, []);

  const handleCloseAttributePreview = () => {
    setIsPreviewAttributeOpened(false);
    setAttribute(null);
  };

  const manageCycleColumns = useMemo(
    () =>
      prepareManageCycleFormColumns(
        classes,
        activeSteps,
        peopleFilters,
        intervalOptions,
        setActiveSteps,
        handleOpenAttributePreview
      ),
    [
      classes,
      activeSteps,
      peopleFilters,
      intervalOptions,
      setActiveSteps,
      handleOpenAttributePreview,
    ]
  );

  const handlePageChange = ({
    newPage,
    newOrdering,
    newSearch,
    newFilters,
    isLoadMore,
  } = {}) => {
    const params = {
      [PAGE]: newPage,
      [ORDERING]: newOrdering?.sortKey,
      ...newFilters,
      ...(newSearch ? { [SEARCH]: newSearch } : {}),
    };

    const query = parseDuplicateParameters(params);

    if (location.search !== `?${query}`) {
      navigate(`/cycles/?${query}`, { replace: true });
    }

    return getCycles(dispatch, isLoadMore, params);
  };

  const onFiltersChange = async newFilters => {
    try {
      setIsLoading(true);
      setPage(defaultPage);
      setSelectedFilters(newFilters);
      await handlePageChange({
        newPage: defaultPage,
        newSearch: search,
        newOrdering: ordering,
        newFilters,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const onSearch = async text => {
    const searchTerm = trimString(text);

    try {
      setPage(defaultPage);
      setSearch(searchTerm);
      setIsLoading(true);
      await handlePageChange({
        newPage: defaultPage,
        newSearch: searchTerm,
        newOrdering: ordering,
        newFilters: selectedFilters,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const onSort = newOrdering => {
    setPage(defaultPage);
    setOrdering(newOrdering);
    handlePageChange({
      newPage: defaultPage,
      newSearch: search,
      newFilters: selectedFilters,
      newOrdering,
    });
  };

  const onLoadMore = () => {
    setPage(page + 1);
    handlePageChange({
      newPage: page + 1,
      newSearch: search,
      newOrdering: ordering,
      newFilters: selectedFilters,
      isLoadMore: true,
    });
  };

  const getInitialData = async () => {
    try {
      const params = parseQueryParams(
        location.search,
        PAGE_WHITELISTED_PARAMS.CYCLES
      );

      if (!isObjectEmpty(params)) {
        const [ascending, descending] = params[ORDERING].split('-');
        const isAscending = !!ascending;
        const initialOrdering = {
          column: getItemById(
            gridHeaders,
            isAscending ? ascending : descending,
            'sortAs'
          )?.rowKey,
          asc: isAscending,
          sortKey: params[ORDERING],
        };
        const initialSearch = params[SEARCH]
          ? decodeURIComponent(params[SEARCH])
          : '';
        const initialFilters = {
          [CREATOR]: params[CREATOR]
            ? [
                ...(isArray(params[CREATOR])
                  ? params[CREATOR]
                  : [params[CREATOR]]),
              ]
            : [],
          [STATUS]: params[STATUS] ? [params[STATUS]] : [],
        };
        setOrdering(initialOrdering);
        setSearch(initialSearch);
        setSelectedFilters(initialFilters);
        await Promise.all([
          handlePageChange({
            newPage: page,
            newOrdering: initialOrdering,
            newSearch: initialSearch,
            newFilters: initialFilters,
          }),
          getAttributesWithQuestions(dispatch, {
            with_questions: true,
            [PARAMS.EXCLUDE_ATTRIBUTE_TYPE]: ATTRIBUTE_TYPES_MAP.ENPS.id,
          }),
          getJobTitles(dispatch),
          getTagsCategories(dispatch),
          getLevelsAndTracks(dispatch),
          getDivisionsAndDepartments(dispatch),
        ]);
      } else {
        await Promise.all([
          handlePageChange({
            newPage: page,
            newOrdering: ordering,
            newSearch: search,
            newFilters: selectedFilters,
          }),
          getAttributesWithQuestions(dispatch, {
            with_questions: true,
            [PARAMS.EXCLUDE_ATTRIBUTE_TYPE]: ATTRIBUTE_TYPES_MAP.ENPS.id,
          }),
          getJobTitles(dispatch),
          getTagsCategories(dispatch),
          getLevelsAndTracks(dispatch),
          getDivisionsAndDepartments(dispatch),
        ]);
      }
    } finally {
      setIsInitialLoad(false);
    }
  };

  const cleanup = useCallback(() => {
    dispatch(clearAttributesWithQuestions());
    dispatch(clearCycles());
    dispatch(clearLevelsAndTracks());
    dispatch(clearDivisionsAndDepartments());
    dispatch(clearJobTitles());
    dispatch(clearTagsCategories());
  }, [dispatch]);

  const handleOpenManageCycle = () => setIsManageCycleOpened(true);

  const handleCloseManageCycle = () => {
    setIsManageCycleOpened(false);
    setActiveSteps({
      [EMAIL_STEP]: false,
      [SELF_REVIEW_STEP]: false,
      [TOP_DOWN_STEP]: false,
      [DOWN_UP_STEP]: false,
    });
    setSelectedCycle(null);
  };

  const handleSaveManageCycle = async values => {
    if (selectedCycle) {
      await http.patch(api_cycle(selectedCycle.id), {
        [TITLE]: values[TITLE],
        [USERS]: getObjectToNumberArray(values[USERS]),
        [IS_REPEATING]: values[IS_REPEATING],
        ...(values[IS_REPEATING]
          ? {
              [REPEAT_INTERVAL_UNIT]: values[REPEAT_INTERVAL_UNIT],
              [REPEAT_INTERVAL]: values[REPEAT_INTERVAL],
            }
          : {}),
      });
      await Promise.all([
        // EMAIL STEP
        ...(values[EMAIL_STEP] && !initialCycleFormData[EMAIL_STEP]
          ? [
              http.post(
                api_cycle_steps(selectedCycle.id),
                prepareStepForSave(EMAIL_STEP, values[EMAIL_STEP], values)
              ),
            ]
          : []),
        ...(!values[EMAIL_STEP] && initialCycleFormData[EMAIL_STEP]
          ? [
              http.delete(
                api_cycle_step(
                  selectedCycle.id,
                  initialCycleFormData[EMAIL_STEP].id
                )
              ),
            ]
          : []),
        ...(values[EMAIL_STEP] && initialCycleFormData[EMAIL_STEP]
          ? [
              http.patch(
                api_cycle_step(
                  selectedCycle.id,
                  initialCycleFormData[EMAIL_STEP].id
                ),
                prepareStepForSave(EMAIL_STEP, values[EMAIL_STEP], values)
              ),
            ]
          : []),
        // SELF REVIEW STEP
        ...(values[SELF_REVIEW_STEP] && !initialCycleFormData[SELF_REVIEW_STEP]
          ? [
              http.post(
                api_cycle_steps(selectedCycle.id),
                prepareStepForSave(
                  SELF_REVIEW_STEP,
                  values[SELF_REVIEW_STEP],
                  values
                )
              ),
            ]
          : []),
        ...(!values[SELF_REVIEW_STEP] && initialCycleFormData[SELF_REVIEW_STEP]
          ? [
              http.delete(
                api_cycle_step(
                  selectedCycle.id,
                  initialCycleFormData[SELF_REVIEW_STEP].id
                )
              ),
            ]
          : []),
        ...(values[SELF_REVIEW_STEP] && initialCycleFormData[SELF_REVIEW_STEP]
          ? [
              http.patch(
                api_cycle_step(
                  selectedCycle.id,
                  initialCycleFormData[SELF_REVIEW_STEP].id
                ),
                prepareStepForSave(
                  SELF_REVIEW_STEP,
                  values[SELF_REVIEW_STEP],
                  values
                )
              ),
            ]
          : []),
        // TOP DOWN STEP
        ...(values[TOP_DOWN_STEP] && !initialCycleFormData[TOP_DOWN_STEP]
          ? [
              http.post(
                api_cycle_steps(selectedCycle.id),
                prepareStepForSave(TOP_DOWN_STEP, values[TOP_DOWN_STEP], values)
              ),
            ]
          : []),
        ...(!values[TOP_DOWN_STEP] && initialCycleFormData[TOP_DOWN_STEP]
          ? [
              http.delete(
                api_cycle_step(
                  selectedCycle.id,
                  initialCycleFormData[TOP_DOWN_STEP].id
                )
              ),
            ]
          : []),
        ...(values[TOP_DOWN_STEP] && initialCycleFormData[TOP_DOWN_STEP]
          ? [
              http.patch(
                api_cycle_step(
                  selectedCycle.id,
                  initialCycleFormData[TOP_DOWN_STEP].id
                ),
                prepareStepForSave(TOP_DOWN_STEP, values[TOP_DOWN_STEP], values)
              ),
            ]
          : []),
        // DOWN UP STEP
        ...(values[DOWN_UP_STEP] && !initialCycleFormData[DOWN_UP_STEP]
          ? [
              http.post(
                api_cycle_steps(selectedCycle.id),
                prepareStepForSave(DOWN_UP_STEP, values[DOWN_UP_STEP], values)
              ),
            ]
          : []),
        ...(!values[DOWN_UP_STEP] && initialCycleFormData[DOWN_UP_STEP]
          ? [
              http.delete(
                api_cycle_step(
                  selectedCycle.id,
                  initialCycleFormData[DOWN_UP_STEP].id
                )
              ),
            ]
          : []),
        ...(values[DOWN_UP_STEP] && initialCycleFormData[DOWN_UP_STEP]
          ? [
              http.patch(
                api_cycle_step(
                  selectedCycle.id,
                  initialCycleFormData[DOWN_UP_STEP].id
                ),
                prepareStepForSave(DOWN_UP_STEP, values[DOWN_UP_STEP], values)
              ),
            ]
          : []),
      ]);

      showSuccessMessage(translations.successMessages.edit);
    } else {
      const { data } = await http.post(API_CYCLES, {
        [TITLE]: values[TITLE],
        [USERS]: getObjectToNumberArray(values[USERS]),
        [IS_REPEATING]: values[IS_REPEATING],
        ...(values[IS_REPEATING]
          ? {
              [REPEAT_INTERVAL_UNIT]: values[REPEAT_INTERVAL_UNIT],
              [REPEAT_INTERVAL]: values[REPEAT_INTERVAL],
            }
          : {}),
      });

      await Promise.all([
        ...(values[EMAIL_STEP]
          ? [
              http.post(
                api_cycle_steps(data.id),
                prepareStepForSave(EMAIL_STEP, values[EMAIL_STEP], values)
              ),
            ]
          : []),
        ...(values[SELF_REVIEW_STEP]
          ? [
              http.post(
                api_cycle_steps(data.id),
                prepareStepForSave(SELF_REVIEW_STEP, values[SELF_REVIEW_STEP])
              ),
            ]
          : []),
        ...(values[TOP_DOWN_STEP]
          ? [
              http.post(
                api_cycle_steps(data.id),
                prepareStepForSave(TOP_DOWN_STEP, values[TOP_DOWN_STEP])
              ),
            ]
          : []),
        ...(values[DOWN_UP_STEP]
          ? [
              http.post(
                api_cycle_steps(data.id),
                prepareStepForSave(DOWN_UP_STEP, values[DOWN_UP_STEP])
              ),
            ]
          : []),
      ]);

      showSuccessMessage(translations.successMessages.create);
    }

    setPage(defaultPage);
    handlePageChange({
      newPage: defaultPage,
      newOrdering: ordering,
      newSearch: search,
      newFilters: selectedFilters,
    });

    return Promise.resolve();
  };

  const handleEditCycle = cycleId => async () => {
    const { data } = await http.get(api_cycle(cycleId));

    setSelectedCycle(data);
    setActiveSteps({
      [EMAIL_STEP]: !!getItemById(data.steps, EMAIL_STEP, 'type'),
      [SELF_REVIEW_STEP]: !!getItemById(data.steps, SELF_REVIEW_STEP, 'type'),
      [TOP_DOWN_STEP]: !!getItemById(data.steps, TOP_DOWN_STEP, 'type'),
      [DOWN_UP_STEP]: !!getItemById(data.steps, DOWN_UP_STEP, 'type'),
    });
    handleOpenManageCycle();
  };

  const handleDeleteCycle = cycle => () => {
    setSelectedCycleId(cycle.id);
    setIsDeleteCycleOpened(true);
  };

  const handleCloseDeleteCycle = () => {
    setSelectedCycleId(null);
    setIsDeleteCycleOpened(false);
  };

  const onDeleteCycle = async () => {
    try {
      await http.delete(api_cycle(selectedCycleId));
      setPage(defaultPage);
      handlePageChange({
        newPage: defaultPage,
        newSearch: search,
        newOrdering: ordering,
        newFilters: selectedFilters,
      });
      showSuccessMessage(translations.deleteCycleDialog.deleteSuccess);
    } finally {
      handleCloseDeleteCycle();
    }
  };

  const handleViewCycleReviews = cycleId => {
    getCycleReviews(dispatch, cycleId);
  };

  const prepareReviewDetailsUrl = (cycleId, reviewId) =>
    `/cycles/${cycleId}/reviews/${reviewId}/details`;

  const handleFillReview = async reviewId => {
    const { data } = await getSurveyTrackingById(reviewId);
    const reviewToken = data && data.tracking_token ? data.tracking_token : '';

    if (reviewToken) {
      dispatch(setIsInlineSurvey(true));

      navigate(`/fill-survey/${reviewToken}`);
    }
  };

  const handleSendReminder = reviewId => {
    const params = {
      client_url: `${window.location.origin}/fill-survey`,
      group_id: reviewId,
    };

    http.post(api_survey_send_reminder(reviewId), params).then(() => {
      showSuccessMessage(translations.reminderSent);
    });
  };

  const handleOpenReviewTitleDialog = cycleId => review => {
    setIsReviewTitleDialogOpened(true);
    setSelectedCycleId(cycleId);
    setSelectedReview(review);
  };

  const handleCloseReviewTitleDialog = () => {
    setIsReviewTitleDialogOpened(false);
    setSelectedCycleId(null);
    setSelectedReview(null);
  };

  const onChangeReviewTitle = reviewTitle => {
    return http
      .patch(api_survey(selectedReview.id), { title: reviewTitle })
      .then(() => {
        showSuccessMessage(translations.renameReviewDialog.success);
        handleCloseReviewTitleDialog();

        return getCycleReviews(dispatch, selectedCycleId);
      });
  };

  useEffect(() => {
    getInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  const renderStatus = status => <StatusIndicator status={status} />;

  const renderActions = cycle => (
    <div className={classes.rowActions}>
      <div>
        {cycle.status === CYCLE_STATUSES.PENDING && (
          <ActionButton
            type={ACTION_BUTTON_TYPES.EDIT}
            tooltipText={translations.menuItemLabels.edit}
            onClickHandler={handleEditCycle(cycle.id)}
          />
        )}
      </div>
      <ActionButton
        type={ACTION_BUTTON_TYPES.DELETE}
        tooltipText={translations.menuItemLabels.delete}
        onClickHandler={handleDeleteCycle(cycle)}
      />
    </div>
  );

  const gridTableData = prepareCycleTableData(
    translations,
    canManage,
    cycles?.results || [],
    statuses,
    intervalOptions,
    renderStatus,
    renderActions
  );

  return (
    <PageContainer
      {...rest}
      location={location}
      auth={auth}
      accessControl={accessControl}
      organizationSettings={organizationSettings}
      navigate={navigate}
      translations={translations}
      shouldPassProps={false}
      isFullWidthContent
    >
      {!isInitialLoad && (
        <div>
          <div className={classes.stickyHeader}>
            <div className={classes.description}>
              <Typography variant="body2">
                {translations.description}
              </Typography>
            </div>
            <div className={classes.actionWrapper}>
              <div className={classes.filters}>
                <Filters
                  translations={translations.filters}
                  selectedFilters={selectedFilters}
                  filters={filters}
                  onApplyFilters={onFiltersChange}
                />
              </div>
              <div className={classes.actions}>
                <Search
                  className={classes.search}
                  placeholder={translations.searchPlaceholder}
                  value={search}
                  onChange={onSearch}
                />
                {canManage && (
                  <CustomButton
                    className={classes.addCycleButton}
                    type="addRoundedNew"
                    onClick={handleOpenManageCycle}
                  >
                    {translations.addCycle}
                  </CustomButton>
                )}
              </div>
            </div>
          </div>
          {isArray(cycles.results) && !isArrayEmpty(cycles.results) && (
            <CyclesTable
              className={classes.table}
              translations={translations}
              initialSort={ordering}
              headerActions={gridHeaderActions}
              headers={gridHeaders}
              hasLoadMore={hasLoadMore}
              rows={gridTableData}
              reviewTypes={reviewTypes}
              canManage={canManage}
              onViewReviews={handleViewCycleReviews}
              prepareReviewDetailsUrl={prepareReviewDetailsUrl}
              onFillReview={handleFillReview}
              onSendReminder={handleSendReminder}
              onRenameReview={handleOpenReviewTitleDialog}
              onLoadMore={onLoadMore}
              onSort={onSort}
            />
          )}
          {!isLoading &&
            isArray(cycles.results) &&
            isArrayEmpty(cycles.results) && (
              <NotificationCard
                title={
                  search || isActiveFilter ? '' : translations.noResults.title
                }
                content={
                  search || isActiveFilter
                    ? translations.noResults.search
                    : translations.noResults.message
                }
              />
            )}

          <CustomFormDrawer
            titleClass={classes.cycleTitle}
            customHeaderClass={classes.cycleFormHeader}
            customColumnsClass={classes.columns}
            translations={translations.manageCycle}
            titleIcon={CyclesIcon}
            initialData={initialCycleFormData}
            isOpened={isManageCycleOpened}
            isInitialValid={!!selectedCycle}
            columns={manageCycleColumns}
            attributes={attributesWithQuestions}
            intervalOptions={intervalOptions}
            onClose={handleCloseManageCycle}
            onSave={handleSaveManageCycle}
            hasCancelButton
            isWholeScreen
            hideDelete
          />
          <ChooseNameDialog
            translations={translations.renameReviewDialog}
            title={translations.renameReviewDialog.title}
            description={translations.renameReviewDialog.description}
            isOpened={isReviewTitleDialogOpened}
            initialValue={selectedReview?.title}
            field={SURVEY_TITLE_FIELD}
            onCancel={handleCloseReviewTitleDialog}
            onSave={onChangeReviewTitle}
          />
          {attribute && (
            <PreviewAttributeDialog
              isOpened={isPreviewAttributeOpened}
              translations={translations.previewAttributeDialog}
              attribute={attribute}
              onClose={handleCloseAttributePreview}
            />
          )}
          <AlertDialog
            isOpened={isDeleteCycleOpened}
            translations={translations.deleteCycleDialog}
            onClose={handleCloseDeleteCycle}
            onConfirm={onDeleteCycle}
            isWarning
          />
        </div>
      )}
    </PageContainer>
  );
};

CyclesPage.propTypes = {
  location: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
  auth: PropTypes.object.isRequired,
  accessControl: PropTypes.object.isRequired,
  organizationSettings: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  setDialogVisibility: PropTypes.func.isRequired,
};

export default memo(CyclesPage);
